import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { fetchCarts, fetchCart, type Cart, type CartState } from "@/services/carts";
import { fetchReturns, fetchReturnItems, type ReturnEvent } from "@/services/stock";
import { uploadMedia } from "@/services/media";
import { useFormat } from "@/lib/format";
import { shrinkImage } from "@/lib/shrinkImage";
import { flushCarts, pendingSaves, saveCart, type PendingSave } from "@/lib/cartQueue";
import { CART_FORMATS, MAX_CART_LABELS, cartId, type CartFormat } from "@/lib/cartLabelFormats";
import { saveBlob } from "@/lib/download";
import { Toast, useToast } from "@/components/Toast";
import { ReadOnlyArea, ReadOnlyBanner } from "@/components/ReadOnly";

// Z Furniture · Chariots — "Un QR sur chaque chariot : chez le client on l'associe au retour, à
// l'entrepôt on le scanne pour savoir à qui il est." His overview (find a cart, the four counts, the
// past returns with no cart, the carts in use per return, the grid) and his cart page (link it —
// cart i of n, with a photo — its facts, the other carts of the return, the furniture expected,
// arrival, stock intake, release, history).

const STATES: CartState[] = ["free", "on_the_road", "at_warehouse", "lost"];
const MAX_UNITS = 8;

// His `cnum`: "CH-042", "ch 42", "42" → 42, for 1 to 999.
export const cartNumber = (s: string) => {
  const m = /^\s*(?:ch)?[\s-]*0*(\d{1,3})\s*$/i.exec(s);
  const n = m ? Number(m[1]) : 0;
  return n >= 1 && n <= 999 ? n : null;
};

// His `fmtD`: "dd.mm · hh:mm", with "Today" / "Yesterday" / "Tomorrow" / the weekday in front when asked.
function useWhen() {
  const { t, locale } = useT();
  const zurich = (d: Date, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich", ...o }).format(d);
  const dayKey = (d: Date) => zurich(d, { year: "numeric", month: "2-digit", day: "2-digit" });
  return (iso: string | null | undefined, withDay = false) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
      .formatToParts(d).map((x) => [x.type, x.value]));
    const s = `${p.day}.${p.month} · ${p.hour}:${p.minute}`;
    if (!withDay) return s;
    const k = dayKey(d), now = new Date(), day = (n: number) => dayKey(new Date(now.getTime() + n * 864e5));
    const label = k === day(0) ? t("cartx.today") : k === day(-1) ? t("cartx.yesterday") : k === day(1) ? t("cartx.tomorrow")
      : new Intl.DateTimeFormat({ fr: "fr-CH", en: "en-GB", de: "de-CH", it: "it-CH" }[locale], { timeZone: "Europe/Zurich", weekday: "short" }).format(d);
    return `${label} ${s}`;
  };
}

// The saves waiting for network on this phone, sent again when it returns and every 30 seconds.
function useCartQueue(onSent: () => void, toast: (m: string) => void) {
  const [waiting, setWaiting] = useState(() => pendingSaves().length);
  useEffect(() => {
    const update = () => setWaiting(pendingSaves().length);
    const flush = () => { if (pendingSaves().length) void flushCarts().then((refused) => { refused.forEach(toast); update(); onSent(); }); };
    window.addEventListener("cart-queue", update);
    window.addEventListener("online", flush);
    const timer = setInterval(flush, 30000);
    flush();
    return () => { window.removeEventListener("cart-queue", update); window.removeEventListener("online", flush); clearInterval(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return waiting;
}

export default function CartsPage({ warehouseId, initialCart, onCartOpened, onTakeIntoStock }: {
  warehouseId: number | null; initialCart: number | null;
  // Absent for someone without the Stock module: no "Stock intake for this return" then.
  onTakeIntoStock?: (returnEventId: number, warehouseId: number | null) => void;
  // Told once the scanned cart is open, so it is not opened again on the next visit.
  onCartOpened?: () => void;
}) {
  const { t } = useT();
  const when = useWhen();
  const whName = useWarehouseName();
  const qc = useQueryClient();
  const [open, setOpen] = useState<number | null>(initialCart);
  const [typed, setTyped] = useState("");
  const [typedErr, setTypedErr] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [toastMsg, toast] = useToast();
  const print = useCartPrint();
  // His live view: a cart linked on another phone shows up without anyone reloading.
  const carts = useQuery({ queryKey: ["carts", warehouseId], queryFn: () => fetchCarts(warehouseId), refetchInterval: 15000 });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["carts"] }); void qc.invalidateQueries({ queryKey: ["cart"] }); };
  const waiting = useCartQueue(refresh, toast);
  useEffect(() => { if (initialCart) { setOpen(initialCart); onCartOpened?.(); } }, [initialCart]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTyped = () => {
    const n = cartNumber(typed);
    if (!n) { setTypedErr(true); return; }
    setTypedErr(false); setOpen(n); setTyped("");
  };

  const data = carts.data;
  const count = data?.count ?? 200;
  const byNumber = useMemo(() => new Map((data?.carts ?? []).map((c) => [c.number, c])), [data]);
  const numbers = useMemo(() => [...Array.from({ length: count }, (_, i) => i + 1), ...(data?.carts ?? []).map((c) => c.number).filter((n) => n > count)], [count, data]);
  const inWh = (c: Cart) => warehouseId == null || c.myotaku_warehouse_id === warehouseId;
  // His counts: free carts everywhere, the others in the chosen warehouse.
  const counts = useMemo(() => {
    const out: Record<CartState, number> = { free: 0, on_the_road: 0, at_warehouse: 0, lost: 0 };
    for (const n of numbers) { const c = byNumber.get(n); const st = c?.state ?? "free"; if (st === "free" || (c && inWh(c))) out[st]++; }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers, byNumber, warehouseId]);
  // His "Carts in use": one line per return — lost first, then at the warehouse, then on the road, newest first.
  const groups = useMemo(() => {
    const m = new Map<string, Cart[]>();
    for (const c of data?.carts ?? []) {
      if (c.state === "free" || !inWh(c)) continue;
      const k = String(c.myotaku_return_event_id ?? `cart-${c.number}`);
      m.set(k, [...(m.get(k) ?? []), c]);
    }
    const rank = (c: Cart) => ({ lost: 0, at_warehouse: 1, on_the_road: 2, free: 3 })[c.state];
    return [...m.values()].map((g) => g.sort((a, b) => (a.unit_index ?? 0) - (b.unit_index ?? 0)))
      .sort((a, b) => rank(a[0]) - rank(b[0]) || (b[0].linked_at ?? "").localeCompare(a[0].linked_at ?? ""));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, warehouseId]);
  const notYet = (c: Cart) => Math.max(0, (c.unit_count ?? 1) - (data?.ever_by_return[String(c.myotaku_return_event_id)] ?? 0));

  if (open) {
    return (
      <>
        <CartPage number={open} warehouseId={warehouseId} onClose={() => setOpen(null)} onOpen={setOpen} onTakeIntoStock={onTakeIntoStock}
          toast={toast} waiting={waiting} onChanged={refresh}
          reprint={data ? <Reprint number={open} qrBase={data.label_url} print={print} /> : null} />
        <Toast message={toastMsg} />
      </>
    );
  }

  return (
    <div className="space-y-5">
      <Toast message={toastMsg} />
      {waiting > 0 && <p className="rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900 ring-1 ring-amber-200">⏳ {t(waiting === 1 ? "cartx.waiting1" : "cartx.waitingN", { n: waiting })}</p>}
      <p className="text-sm text-slate-500">{t("carts.sub")}</p>
      <form onSubmit={(e) => { e.preventDefault(); openTyped(); }} className="flex flex-wrap items-center gap-2">
        <input value={typed} onChange={(e) => { setTyped(e.target.value); setTypedErr(false); }} placeholder={t("carts.numberPh")} inputMode="numeric"
          className="w-36 rounded-lg border border-slate-300 px-3 py-2 text-sm" aria-label={t("carts.number")} />
        <button className="rounded-lg bg-orange-600 px-4 py-2 text-sm text-white">{t("carts.open")}</button>
        <button type="button" onClick={() => setPrinting(!printing)} aria-expanded={printing} className="rounded-lg bg-white px-3 py-2 text-sm ring-1 ring-slate-300">{t("cartLabel.open")}</button>
        {typedErr && <span role="alert" className="text-sm text-red-600">{t("cartx.unknownNumber")}</span>}
      </form>
      {printing && data && <PrintPanel count={count} qrBase={data.label_url} print={print} onClose={() => setPrinting(false)} />}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {STATES.map((s) => (
          <div key={s} className="rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-200">
            <div className="text-xs text-slate-500">{t(`carts.st.${s}`)}</div>
            <div className="text-2xl font-semibold tabular-nums text-slate-900">{counts[s]}</div>
          </div>
        ))}
      </div>

      <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <h3 className="font-medium text-slate-900">{t("carts.missing")}</h3>
        {data?.returns_without_cart.length ? (
          <>
            <p className="mb-2 text-xs text-slate-500">{t("carts.missingHint")}</p>
            <ul className="space-y-1.5">
              {data.returns_without_cart.map((r) => (
                <li key={r.id} className="rounded-lg bg-amber-50 px-3 py-2 text-sm ring-1 ring-amber-200">
                  <b>{r.client_name || r.title}</b> <span className="font-mono text-xs">#{r.id}</span>
                  <div className="text-xs text-slate-600">{[r.title, [when(r.starts_at, true), r.warehouse?.name, r.type_label].filter(Boolean).join(" · ")].filter(Boolean).join(" — ")}</div>
                </li>
              ))}
            </ul>
          </>
        ) : <p className="text-sm text-slate-500">{t("cartx.missingNone")}</p>}
      </section>

      <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
        <h3 className="border-b border-slate-100 px-4 py-3 font-medium text-slate-900">{t("carts.linked")}</h3>
        {groups.length === 0 && <p className="px-4 py-4 text-sm text-slate-400">{t("carts.linkedNone")}</p>}
        <ul className="divide-y divide-slate-100">
          {groups.map((g) => {
            const c = g[0], missing = notYet(c);
            return (
              <li key={c.number} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900"><b>{c.client_name || "—"}</b>{c.myotaku_return_event_id && <span className="ml-1 font-mono text-xs text-slate-500">#{c.myotaku_return_event_id}</span>}</div>
                  <div className="text-xs text-slate-500">{[c.return_title, whName(c.myotaku_warehouse_id)].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="text-xs text-slate-500">{[when(c.linked_at), c.linked_by].filter(Boolean).join(" · ")}</div>
                <div className="flex flex-wrap gap-1">
                  {g.map((x) => <CartChip key={x.number} cart={x} onOpen={setOpen} />)}
                  {missing > 0 && <span className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500">{t("cartx.notYet", { n: missing })}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <details open className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <summary className="cursor-pointer font-medium text-slate-900">{t("carts.grid", { n: count })}</summary>
        <p className="mb-2 mt-1 text-xs text-slate-500">{t("carts.gridHint")}</p>
        <div className="mb-2 flex flex-wrap gap-3 text-xs text-slate-600">
          {STATES.map((s) => <span key={s} className="inline-flex items-center gap-1.5"><i className={`inline-block h-3 w-3 rounded ${stateTone(s)}`} />{t(`carts.st.${s}`)}</span>)}
        </div>
        <div className="grid grid-cols-8 gap-1 sm:grid-cols-10 lg:grid-cols-[repeat(20,minmax(0,1fr))]">
          {numbers.map((n) => {
            const c = byNumber.get(n), st = c?.state ?? "free";
            return (
              <button key={n} onClick={() => setOpen(n)} title={[cartId(n), t(`carts.st.${st}`), c?.status === "linked" ? c.client_name : null].filter(Boolean).join(" · ")}
                className={`rounded py-1 font-mono text-[11px] ${stateTone(st)}`}>
                {String(n).padStart(3, "0")}
              </button>
            );
          })}
        </div>
      </details>
    </div>
  );
}

// The warehouses' names, as the planning feed gives them.
function useWarehouseName() {
  const returns = useQuery({ queryKey: ["stock-returns", null], queryFn: () => fetchReturns(null) });
  const names = new Map((returns.data?.warehouses ?? []).map((w) => [w.id, w.name]));
  return (id: number | null) => (id == null ? "" : names.get(id) ?? `#${id}`);
}

function CartChip({ cart, onOpen }: { cart: Cart; onOpen: (n: number) => void }) {
  const { t } = useT();
  return (
    <button onClick={() => onOpen(cart.number)} title={t(`carts.st.${cart.state}`)}
      className={`rounded-full px-2 py-0.5 font-mono text-xs ${stateTone(cart.state)}`}>
      {cart.label}{(cart.unit_count ?? 1) > 1 ? ` · ${cart.unit_index}/${cart.unit_count}` : ""}
    </button>
  );
}

function StateBadge({ state, big }: { state: CartState; big?: boolean }) {
  const { t } = useT();
  return <span className={`rounded px-2 py-0.5 ${big ? "text-sm font-medium" : "text-xs"} ${stateTone(state)}`}>{t(`carts.st.${state}`)}</span>;
}

function stateTone(s: CartState) {
  if (s === "at_warehouse") return "bg-orange-100 text-orange-800";
  if (s === "on_the_road") return "bg-sky-100 text-sky-800";
  if (s === "lost") return "bg-red-100 text-red-800";
  return "bg-slate-100 text-slate-500";
}

// ---- one cart ------------------------------------------------------------------------------------

function CartPage({ number, warehouseId, onClose, onOpen, onTakeIntoStock, toast, waiting, onChanged, reprint }: {
  number: number; warehouseId: number | null; onClose: () => void; onOpen: (n: number) => void; onTakeIntoStock?: (id: number, warehouseId: number | null) => void;
  toast: (m: string) => void; waiting: number; onChanged: () => void; reprint: React.ReactNode;
}) {
  const { t } = useT();
  const when = useWhen();
  const whName = useWarehouseName();
  const { dateTime } = useFormat();
  const cart = useQuery({ queryKey: ["cart", number], queryFn: () => fetchCart(number), refetchInterval: 15000 });
  const [relink, setRelink] = useState(false);
  const [confirmFree, setConfirmFree] = useState(false);
  const c = cart.data;
  const label = cartId(number);
  const act = useMutation({
    mutationFn: (p: PendingSave & { ok: string }) => saveCart(p).then((r) => ({ r, ok: p.ok })),
    onSuccess: ({ r, ok }) => { toast(r === "queued" ? t("cartx.queued") : ok); setRelink(false); setConfirmFree(false); onChanged(); },
    onError: (e: Error) => toast(e.message),
  });
  const items = useQuery({
    queryKey: ["return-items", c?.myotaku_return_event_id], enabled: !!c?.myotaku_return_event_id && c.status === "linked",
    queryFn: () => fetchReturnItems(c!.myotaku_return_event_id!),
  });
  const notYet = c ? Math.max(0, (c.unit_count ?? 1) - (c.ever ?? 0)) : 0;
  const events = c?.events ?? [];

  return (
    <section className="space-y-4">
      <button onClick={onClose} className="text-sm text-slate-500 underline">{t("carts.back")}</button>
      {waiting > 0 && <p className="rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-900 ring-1 ring-amber-200">⏳ {t(waiting === 1 ? "cartx.waiting1" : "cartx.waitingN", { n: waiting })}</p>}
      <div className="flex items-center gap-3">
        <div><div className="text-xs uppercase tracking-wide text-slate-400">{t("cartx.cart")}</div><div className="font-mono text-3xl font-bold">{label}</div></div>
        {c && <StateBadge state={c.state} big />}
      </div>
      {cart.error && <p className="text-sm text-red-600">{(cart.error as Error).message}</p>}
      <ReadOnlyBanner module="carts" text={t("cartx.readOnly")} />

      {c && c.status === "free" && (
        <p className="text-sm text-slate-500">{c.freed_at && c.client_name ? t("cartx.lastClient", { client: c.client_name, date: when(c.freed_at).split(" · ")[0] }) : t("cartx.never")}</p>
      )}
      {c && (c.status === "free" || relink) && (
        <ReadOnlyArea module="carts">
          <LinkForm number={number} cart={c} warehouseId={warehouseId} relink={relink} onCancel={() => setRelink(false)} busy={act.isPending}
            onLink={(body, client) => act.mutate({ number, action: "link", body, ok: t("cartx.linkedOk", { cart: label, client }) })} />
        </ReadOnlyArea>
      )}

      {c && c.status === "linked" && !relink && (
        <>
          <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <Fact label={t("cartx.client")} value={c.client_name || "—"} />
              <Fact label={t("cartx.event")} value={<span className="font-mono">{c.myotaku_return_event_id ? `#${c.myotaku_return_event_id}` : "—"}{c.linked_by_hand ? ` · ${t("cartx.byHand")}` : ""}</span>} />
              {c.return_starts_at && <Fact label={c.return_type || t("cartx.event")} value={when(c.return_starts_at, true)} />}
              <Fact label={t("cartx.linkedBy")} value={[c.linked_by, when(c.linked_at)].filter(Boolean).join(" · ")} />
              {c.arrived_at && <Fact label={t("cartx.arrived")} value={[c.arrived_by, when(c.arrived_at)].filter(Boolean).join(" · ")} />}
              {c.myotaku_warehouse_id && <Fact label={t("cartx.warehouse")} value={whName(c.myotaku_warehouse_id)} />}
            </dl>
            {c.return_title && <p className="mt-2 text-sm text-slate-600">{c.return_title}</p>}
            {c.photo?.url && <img src={c.photo.thumb_url || c.photo.url} alt={t("cartx.photo")} className="mt-3 max-h-72 rounded-lg ring-1 ring-slate-200" />}
            <h4 className="mt-4 text-sm font-medium text-slate-700">{t("cartx.others")} · {t("cartx.iOfN", { i: c.unit_index ?? 1, n: c.unit_count ?? 1 })}</h4>
            <div className="mt-1 flex flex-wrap gap-1">
              {(c.return_carts ?? [c]).map((x) => <CartChip key={x.number} cart={x} onOpen={onOpen} />)}
              {notYet > 0 && <span className="rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500">{t("cartx.notYet", { n: notYet })}</span>}
            </div>
            <h4 className="mt-4 text-sm font-medium text-slate-700">{t("cartx.items", { n: items.data?.items.length ?? 0 })}</h4>
            {items.data?.items.length ? (
              <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
                {items.data.items.map((i) => <li key={i.product_id}><b className="tabular-nums">{i.expected}×</b> {i.name}{i.rooms.length ? <span className="text-xs text-slate-500"> · {i.rooms.join(", ")}</span> : null}</li>)}
              </ul>
            ) : <p className="mt-1 text-sm text-slate-500">{t("cartx.noItems")}</p>}
          </div>
          <ReadOnlyArea module="carts">
          <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {!c.arrived_at && <button disabled={act.isPending} onClick={() => act.mutate({ number, action: "arrive", body: {}, ok: t("cartx.arrivedOk", { cart: label }) })}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white">{t("carts.arrive")}</button>}
            {c.myotaku_return_event_id && onTakeIntoStock && (
              <button disabled={act.isPending} onClick={() => { act.mutate({ number, action: "arrive", body: { then: "stock" }, ok: "" }); onTakeIntoStock(c.myotaku_return_event_id!, c.myotaku_warehouse_id); }}
                className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white">{t("cartx.toStock")}</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {confirmFree ? (
              <>
                <button onClick={() => act.mutate({ number, action: "free", body: {}, ok: t("cartx.freedOk", { cart: label }) })}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white">{t("cartx.freeConfirm", { cart: label })}</button>
                <button onClick={() => setConfirmFree(false)} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cancel")}</button>
              </>
            ) : (
              <>
                <button onClick={() => setConfirmFree(true)} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cartx.free")}</button>
                <button onClick={() => setRelink(true)} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cartx.relink")}</button>
              </>
            )}
          </div>
          </div>
          </ReadOnlyArea>
        </>
      )}

      <div className="border-t border-slate-100 pt-3">{reprint}</div>

      {events.length > 0 && (
        <details className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <summary className="cursor-pointer text-sm"><b>{t("cartx.history")}</b> ({events.length})</summary>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {events.map((e, i) => (
              <li key={i}><b>{t(`cartx.h.${e.action}`)}</b> · {dateTime(e.at)}{e.client_name ? ` · ${e.client_name}` : ""}{e.return_event_id ? ` · #${e.return_event_id}` : ""}{e.by_name ? ` · ${e.by_name}` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><dt className="text-xs text-slate-500">{label}</dt><dd className="text-slate-900">{value}</dd></div>;
}

// His picker: the return's warehouse (the cart's, else the overview's), its returns from three days
// ago to two days ahead closest to now first, or a search across every return (30 at most); the
// return typed by hand; cart i of n for this client; the photo of the loaded cart.
function LinkForm({ number, cart, warehouseId, relink, onCancel, onLink, busy }: {
  number: number; cart: Cart; warehouseId: number | null; relink: boolean; busy: boolean;
  onCancel: () => void; onLink: (body: Record<string, unknown>, client: string) => void;
}) {
  const { t } = useT();
  const when = useWhen();
  const returns = useQuery({ queryKey: ["stock-returns", null], queryFn: () => fetchReturns(null) });
  const allCarts = useQuery({ queryKey: ["carts", null], queryFn: () => fetchCarts(null) });
  const [wh, setWh] = useState<number | null>(cart.myotaku_warehouse_id ?? warehouseId ?? (Number(localStorage.getItem("myotaku-warehouse:cart-wh")) || null));
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<ReturnEvent | null>(null);
  const [manual, setManual] = useState(false);
  const [manEv, setManEv] = useState("");
  const [manClient, setManClient] = useState("");
  const [i, setI] = useState(1);
  const [n, setN] = useState(1);
  const [err, setErr] = useState("");
  const [photo, setPhoto] = useState<{ state: "up" | "ok" | "err"; url: string; id: number | null } | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  // His default when nothing else says which: the first warehouse.
  useEffect(() => { if (wh == null && returns.data?.warehouses.length) setWh(returns.data.warehouses[0].id); }, [wh, returns.data]);
  const others = (id: number) => (allCarts.data?.carts ?? []).filter((x) => x.status === "linked" && x.myotaku_return_event_id === id && x.number !== number);
  const pick = (r: ReturnEvent) => {
    setSel(r); setErr("");
    const cur = others(r.id);
    const nextI = Math.min(MAX_UNITS, cur.length + 1);
    setI(nextI); setN(Math.min(MAX_UNITS, Math.max(nextI, ...cur.map((x) => x.unit_count ?? 1))));
  };
  const chooseWh = (id: number) => { setWh(id); try { localStorage.setItem("myotaku-warehouse:cart-wh", String(id)); } catch { /* this tab only */ } };
  const now = Date.now();
  const all = returns.data?.returns ?? [];
  const needle = q.trim().toLowerCase();
  const list = (needle
    ? all.filter((r) => `${r.client_name ?? ""} ${r.title ?? ""} ${r.id} ${r.type_label ?? ""}`.toLowerCase().includes(needle)).slice(0, 30)
    : all.filter((r) => r.warehouse?.id === wh && r.starts_at && Date.parse(r.starts_at) >= now - 3 * 864e5 - 864e5 && Date.parse(r.starts_at) <= now + 3 * 864e5))
    .slice().sort((a, b) => Math.abs(Date.parse(a.starts_at ?? "") - now) - Math.abs(Date.parse(b.starts_at ?? "") - now));

  const takePhoto = async (file: File) => {
    const local = URL.createObjectURL(file);
    setPhoto({ state: "up", url: local, id: null });
    try {
      const media = await uploadMedia("cart", number, await shrinkImage(file));
      setPhoto({ state: "ok", url: local, id: media.id });
    } catch { setPhoto({ state: "err", url: local, id: null }); }
  };
  const submit = () => {
    let r: { id: number; client: string; title: string | null; type: string | null; starts_at: string | null; warehouse: number | null; contract: number | null; byHand: boolean } | null =
      sel ? { id: sel.id, client: sel.client_name || sel.contract_name || sel.title || "", title: sel.title, type: sel.type_label, starts_at: sel.starts_at,
              warehouse: sel.warehouse?.id ?? wh, contract: sel.contract_id, byHand: false } : null;
    if (!r && manual) {
      const ev = Number(manEv.replace(/\D/g, "")), client = manClient.trim();
      if (!ev || !client) { setErr(t("cartx.manErr")); return; }
      const known = all.find((x) => x.id === ev);
      r = known ? { id: known.id, client: known.client_name || client, title: known.title, type: known.type_label, starts_at: known.starts_at, warehouse: known.warehouse?.id ?? wh, contract: known.contract_id, byHand: false }
        : { id: ev, client, title: null, type: null, starts_at: new Date().toISOString(), warehouse: wh, contract: null, byHand: true };
    }
    if (!r) { setErr(t("cartx.pickFirst")); return; }
    if (i > n) { setErr(t("cartx.nBad")); return; }
    if (photo?.state === "up") return;
    onLink({
      return_event_id: r.id, contract_id: r.contract, client_name: r.client, warehouse_id: r.warehouse, by_hand: r.byHand,
      return_title: r.title, return_type: r.type, return_starts_at: r.starts_at, unit_index: i, unit_count: n,
      photo_media_file_id: photo?.state === "ok" ? photo.id : null,
    }, r.client || `#${r.id}`);
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <h3 className="font-medium text-slate-900">{t("carts.linkT")}</h3>
      {relink && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{t("cartx.relinkWarn", { client: cart.client_name || "—" })}</p>}
      <div className="mt-3 flex flex-wrap gap-1" role="group" aria-label={t("cartx.warehouse")}>
        {(returns.data?.warehouses ?? []).map((w) => (
          <button key={w.id} onClick={() => chooseWh(w.id)} aria-pressed={wh === w.id}
            className={`rounded-full px-3 py-1 text-sm ring-1 ${wh === w.id ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-300"}`}>{w.name}</button>
        ))}
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("carts.search")} className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      <div className="mt-2 max-h-80 space-y-1 overflow-y-auto">
        {returns.isLoading && <p className="text-sm text-slate-400">…</p>}
        {!returns.isLoading && list.length === 0 && <p className="text-sm text-slate-500">{t(needle ? "cartx.noHit" : "cartx.noEv")}</p>}
        {list.map((r) => {
          const k = others(r.id).length;
          return (
            <button key={r.id} onClick={() => pick(r)} aria-pressed={sel?.id === r.id}
              className={`block w-full rounded-lg px-3 py-2 text-left text-sm ring-1 ${sel?.id === r.id ? "bg-orange-50 ring-orange-400" : "bg-white ring-slate-200 hover:bg-slate-50"}`}>
              <b>{r.client_name || r.title}</b> <span className="font-mono text-xs text-slate-500">#{r.id}</span>
              <div className="text-xs text-slate-500">{r.title}</div>
              <div className="text-xs text-slate-500">{[when(r.starts_at, true), r.type_label, needle ? r.warehouse?.name : null].filter(Boolean).join(" · ")}{k ? ` · ${t(k === 1 ? "cartx.cartsOk1" : "cartx.cartsOkN", { n: k })}` : ""}</div>
            </button>
          );
        })}
      </div>
      <details open={manual} onToggle={(e) => setManual((e.target as HTMLDetailsElement).open)} className="mt-3">
        <summary className="cursor-pointer text-sm text-slate-700">{t("cartx.manual")}</summary>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-slate-500">{t("cartx.manEv")}<input value={manEv} onChange={(e) => setManEv(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" /></label>
          <label className="text-xs text-slate-500">{t("cartx.client")}<input value={manClient} onChange={(e) => setManClient(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" /></label>
        </div>
      </details>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        {t("cartx.unitNo")}
        <select value={i} onChange={(e) => setI(Number(e.target.value))} aria-label={t("cartx.unitNo")} className="rounded border border-slate-300 bg-white px-2 py-1">
          {Array.from({ length: MAX_UNITS }, (_, k) => k + 1).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        {t("cartx.of")}
        <select value={n} onChange={(e) => setN(Number(e.target.value))} aria-label={t("cartx.of")} className="rounded border border-slate-300 bg-white px-2 py-1">
          {Array.from({ length: MAX_UNITS }, (_, k) => k + 1).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        {t("cartx.forClient")}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void takePhoto(f); }} />
        {photo ? (
          <>
            <img src={photo.url} alt="" className="h-20 rounded-lg ring-1 ring-slate-200" />
            <span className={`text-xs ${photo.state === "err" ? "text-red-600" : "text-slate-500"}`}>{photo.state === "up" ? t("cartx.photoUp") : photo.state === "err" ? t("cartx.photoErr") : ""}</span>
            <button onClick={() => setPhoto(null)} className="rounded-lg px-2 py-1 text-xs ring-1 ring-slate-300">{t("cartx.photoDel")}</button>
          </>
        ) : (
          <>
            <button onClick={() => camera.current?.click()} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">📷 {t("cartx.photo")}</button>
            <span className="text-xs text-slate-400">{t("cartx.optional")}</span>
          </>
        )}
      </div>
      {err && <p role="alert" className="mt-2 text-sm text-red-600">{err}</p>}
      <div className="mt-3 flex gap-2">
        <button disabled={busy || photo?.state === "up"} onClick={submit} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{t("cartx.doLink", { cart: cartId(number) })}</button>
        {relink && <button onClick={onCancel} className="rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300">{t("cancel")}</button>}
      </div>
    </div>
  );
}

// ---- his cart QR labels (PDF) --------------------------------------------------------------------

// The format and the labels per cart, remembered on this device as his are (default Zebra, 2).
type CartPrint = { fmt: CartFormat; setFmt: (f: CartFormat) => void; cp: number; setCp: (n: number) => void };
const PRINT_KEY = "myotaku-warehouse:cart-labels";
function useCartPrint(): CartPrint {
  const saved = (() => { try { return JSON.parse(localStorage.getItem(PRINT_KEY) || "{}") as { fmt?: string; cp?: number }; } catch { return {}; } })();
  const [fmt, setF] = useState<CartFormat>(saved.fmt && saved.fmt in CART_FORMATS ? (saved.fmt as CartFormat) : "zebra");
  const [cp, setC] = useState(saved.cp && saved.cp >= 1 && saved.cp <= 4 ? saved.cp : 2);
  const keep = (v: { fmt: CartFormat; cp: number }) => { try { localStorage.setItem(PRINT_KEY, JSON.stringify(v)); } catch { /* this tab only */ } };
  return { fmt, cp, setFmt: (f) => { setF(f); keep({ fmt: f, cp }); }, setCp: (n) => { setC(n); keep({ fmt, cp: n }); } };
}


async function downloadCartLabels(nums: number[], fmt: CartFormat, qrBase: string, t: ReturnType<typeof useT>["t"], locale: ReturnType<typeof useT>["locale"]) {
  const { cartLabelsPdf } = await import("@/lib/cartLabels");
  const from = nums[0], to = nums[nums.length - 1];
  saveBlob(cartLabelsPdf(nums, fmt, qrBase, locale),
    from === to ? t("cartLabel.file1", { id: cartId(from), fmt }) : t("cartLabel.fileN", { from: cartId(from), to: cartId(to), fmt }));
}

function FormatSelect({ print }: { print: CartPrint }) {
  const { t } = useT();
  return (
    <select value={print.fmt} onChange={(e) => print.setFmt(e.target.value as CartFormat)} aria-label={t("cartLabel.format")}
      className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
      {(Object.keys(CART_FORMATS) as CartFormat[]).map((f) => <option key={f} value={f}>{t(`cartLabel.fmt.${f}`)}</option>)}
    </select>
  );
}

function PrintPanel({ count, qrBase, print, onClose }: { count: number; qrBase: string; print: CartPrint; onClose: () => void }) {
  const { t, locale } = useT();
  const [from, setFrom] = useState("1");
  const [to, setTo] = useState(String(count));
  const [state, setState] = useState<"idle" | "busy" | "ok" | "err">("idle");
  const a = cartNumber(from), z = cartNumber(to);
  const n = a && z && z >= a ? (z - a + 1) * print.cp : 0;
  const valid = n > 0 && n <= MAX_CART_LABELS;
  const go = async () => {
    if (!valid || !a || !z) return;
    setState("busy");
    try {
      const nums: number[] = [];
      for (let k = a; k <= z; k++) for (let c = 0; c < print.cp; c++) nums.push(k);
      await downloadCartLabels(nums, print.fmt, qrBase, t, locale);
      setState("ok");
    } catch { setState("err"); }
  };
  return (
    <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium text-slate-900">{t("cartLabel.title")}</h3>
        <button onClick={onClose} className="rounded-lg px-3 py-1 text-sm ring-1 ring-slate-300">{t("cartLabel.close")}</button>
      </div>
      <p className="mt-1 text-xs text-slate-500">{t("cartLabel.hint")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span>{t("cartLabel.format")}</span><FormatSelect print={print} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        {t("cartLabel.from")}
        <input value={from} onChange={(e) => setFrom(e.target.value)} inputMode="numeric" className="w-20 rounded-lg border border-slate-300 px-2 py-1.5" />
        {t("cartLabel.to")}
        <input value={to} onChange={(e) => setTo(e.target.value)} inputMode="numeric" className="w-20 rounded-lg border border-slate-300 px-2 py-1.5" />
        <span className="ml-3">{t("cartLabel.copies")}</span>
        <select value={print.cp} onChange={(e) => print.setCp(Number(e.target.value))} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5">
          {[1, 2, 3, 4].map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      {!valid && <p className="mt-2 text-sm text-red-600">{t("cartLabel.range")}</p>}
      {state === "ok" && <p className="mt-2 text-sm text-emerald-700">✅ {t("cartLabel.ok")}</p>}
      {state === "err" && <p className="mt-2 text-sm text-red-600">{t("cartLabel.err")}</p>}
      <button onClick={() => void go()} disabled={!valid || state === "busy"}
        className="mt-3 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
        {state === "busy" ? t("cartLabel.busy") : t(n === 1 ? "cartLabel.print1" : "cartLabel.printN", { n })}
      </button>
    </section>
  );
}

// His "Reprint this cart's label", with the format and the labels per cart.
function Reprint({ number, qrBase, print }: { number: number; qrBase: string; print: CartPrint }) {
  const { t, locale } = useT();
  const [state, setState] = useState<"idle" | "busy" | "ok" | "err">("idle");
  const go = async () => {
    setState("busy");
    try { await downloadCartLabels(Array(print.cp).fill(number), print.fmt, qrBase, t, locale); setState("ok"); } catch { setState("err"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <button onClick={() => void go()} disabled={state === "busy"} className="rounded-lg bg-white px-3 py-1.5 ring-1 ring-slate-300 disabled:opacity-40">
        {state === "busy" ? t("cartLabel.busy") : t("cartLabel.reprint")}
      </button>
      <FormatSelect print={print} />
      <span className="text-slate-500">{t("cartLabel.copiesN", { n: print.cp })}</span>
      {state === "ok" && <span className="text-emerald-700">✅ {t("cartLabel.ok")}</span>}
      {state === "err" && <span className="text-red-600">{t("cartLabel.err")}</span>}
    </div>
  );
}
